import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, HeartHandshake, Network } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

export default function StudentPromise() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <section className="py-32 bg-slate-50 relative overflow-hidden">
            {/* Subtle Grid Texture */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]"></div>

            {/* Minimal Background Glow to add 'Life' */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-indigo-50/30 rounded-full blur-[120px] pointer-events-none"></div>

            {/* Subtle background flair */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Highly-Premium Trust Signal - CENTERED & COMPACT */}
                <div className="max-w-4xl mx-auto mb-20 p-[1px] rounded-[40px] bg-gradient-to-r from-indigo-500 via-violet-500 to-indigo-500 backdrop-blur-3xl shadow-2xl overflow-hidden animate-fade-in">
                    <div className="bg-indigo-50/90 rounded-[39px] px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="flex -space-x-3">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-indigo-500 flex items-center justify-center text-[10px] font-bold text-white shadow-lg overflow-hidden">
                                        <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="user" className="w-full h-full object-cover"  loading="lazy" decoding="async"/>
                                    </div>
                                ))}
                                <div className="w-10 h-10 rounded-full border-2 border-white bg-indigo-600 flex items-center justify-center text-[10px] font-bold text-white shadow-xl z-10">
                                    2.4k+
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <div className="flex items-center gap-1 text-amber-500">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Shield key={s} className="w-3.5 h-3.5 fill-current" />
                                    ))}
                                </div>
                                <span className="text-sm font-black text-slate-900 tracking-tight">Trusted by 2,400+ Students this month.</span>
                            </div>
                        </div>

                        <button
                            onClick={() => navigate('/eagerminds')}
                            className="group flex items-center gap-3 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-black text-sm hover:bg-slate-900 transition-all duration-300 shadow-lg shadow-indigo-600/20 hover:scale-105 active:scale-95"
                        >
                            Join the Movement
                            <Network className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                        </button>
                    </div>
                </div>

                <div className="text-center mb-20">
                    <span className="inline-block py-1.5 px-4 rounded-full bg-indigo-50 text-indigo-700 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-6 border border-indigo-100 shadow-sm">
                        {t('home.promise.badge')}
                    </span>
                    <h2 className="text-4xl md:text-6xl font-black text-slate-950 mb-8 tracking-tight">
                        <Trans
                            i18nKey="home.promise.title"
                            components={{
                                0: <span className="text-indigo-600" />
                            }}
                        />
                    </h2>
                    <p className="max-w-3xl mx-auto text-lg md:text-xl text-slate-600 leading-relaxed font-semibold">
                        {t('home.promise.desc')}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Promise 1: No Spam */}
                    <div className="group bg-white p-10 rounded-[40px] border border-slate-200/50 hover:border-indigo-100 transition-all duration-500 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)] text-center">
                        <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-3xl flex items-center justify-center mb-8 mx-auto group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-sm">
                            <Shield className="w-10 h-10" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-950 mb-4 tracking-tight">{t('home.promise.promises.zero_spam_title')}</h3>
                        <p className="text-slate-500 text-lg leading-relaxed font-medium">
                            <Trans
                                i18nKey="home.promise.promises.zero_spam_desc"
                                components={{
                                    0: <span className="font-black text-emerald-600" />
                                }}
                            />
                        </p>
                    </div>

                    {/* Promise 2: Unbiased Data */}
                    <div className="group bg-white p-10 rounded-[40px] border border-slate-200/50 hover:border-indigo-100 transition-all duration-500 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)] text-center">
                        <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mb-8 mx-auto group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-sm">
                            <Eye className="w-10 h-10" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-950 mb-4 tracking-tight">{t('home.promise.promises.reality_title')}</h3>
                        <p className="text-slate-500 text-lg leading-relaxed font-medium">
                            <Trans
                                i18nKey="home.promise.promises.reality_desc"
                                components={{
                                    0: <span className="font-black text-rose-600" />
                                }}
                            />
                        </p>
                    </div>

                    {/* Promise 3: Support */}
                    <div className="group bg-white p-10 rounded-[40px] border border-slate-200/50 hover:border-indigo-100 transition-all duration-500 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)] text-center">
                        <div className="w-20 h-20 bg-rose-50 text-rose-600 rounded-3xl flex items-center justify-center mb-8 mx-auto group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-sm">
                            <HeartHandshake className="w-10 h-10" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-950 mb-4 tracking-tight">{t('home.promise.promises.guarantee_title')}</h3>
                        <p className="text-slate-500 text-lg leading-relaxed font-medium">
                            <Trans
                                i18nKey="home.promise.promises.guarantee_desc"
                                components={{
                                    0: <span className="font-black text-indigo-600" />
                                }}
                            />
                        </p>
                    </div>
                </div>

            </div>
        </section>
    );
}
